/**
 * UoM 4A.1 — Shared quantity formatter.
 *
 * Pure functions usable from both the Node server and the React client.
 * Renders a base-unit quantity in the item's preferred display unit by
 * walking the `unit_conversions` chain. Falls back gracefully when no
 * conversions exist for the requested unit.
 */

export type FormatQtyItem = {
  baseUnitId?: string | null;
  defaultDisplayUnitId?: string | null;
};

export type FormatQtyUnit = {
  id: string;
  abbreviation?: string | null;
  name?: string | null;
};

export type FormatQtyConversion = {
  unitId: string;
  baseUnitsPer: number | string;
};

export type FormatQtyOptions = {
  displayUnitId?: string | null;
  showSecondary?: boolean;
  locale?: "en" | "ar";
  precision?: number;
};

const FALLBACK_UNIT_LABEL = "";

function toNumber(v: number | string | null | undefined): number {
  if (v == null) return NaN;
  if (typeof v === "number") return v;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : NaN;
}

function formatNumber(value: number, locale: "en" | "ar", precision: number): string {
  if (!Number.isFinite(value)) return "—";
  // Strip trailing zeros up to `precision` decimal places while still
  // localizing thousand separators.
  const localeTag = locale === "ar" ? "ar-EG" : "en-US";
  return new Intl.NumberFormat(localeTag, {
    maximumFractionDigits: precision,
    minimumFractionDigits: 0,
    useGrouping: true,
  }).format(value);
}

function unitLabel(unit: FormatQtyUnit | undefined): string {
  if (!unit) return FALLBACK_UNIT_LABEL;
  return (unit.abbreviation || unit.name || "").toString();
}

/**
 * Render a base-unit-denominated quantity in the item's preferred display
 * unit. When the display unit equals the base unit the helper simply
 * prints `<qty> <baseAbbr>`. Otherwise it divides by the conversion's
 * `baseUnitsPer` to get the display-unit quantity.
 *
 * @param qty Quantity, always denominated in the item's base unit.
 *   Accepts a numeric string (Drizzle `numeric` returns strings).
 * @param item Minimum item shape (`baseUnitId`, `defaultDisplayUnitId`).
 * @param units All units known to the workspace.
 * @param conversions Unit-conversion rows for this item (any subset).
 * @param opts Optional overrides: explicit display unit, secondary
 *   breakdown, locale, decimal precision.
 */
export function formatQty(
  qty: number | string,
  item: FormatQtyItem,
  units: FormatQtyUnit[],
  conversions: FormatQtyConversion[],
  opts: FormatQtyOptions = {},
): string {
  const numericQty = toNumber(qty);
  const locale = opts.locale ?? "en";
  const precision = opts.precision ?? 2;

  if (!Number.isFinite(numericQty)) {
    return "—";
  }

  const baseUnitId = item.baseUnitId ?? null;
  const requestedDisplayId =
    opts.displayUnitId ?? item.defaultDisplayUnitId ?? baseUnitId ?? null;

  const findUnit = (id: string | null): FormatQtyUnit | undefined => {
    if (!id) return undefined;
    return units.find((u) => u.id === id);
  };

  const baseUnit = findUnit(baseUnitId);
  const baseAbbr = unitLabel(baseUnit);

  // If we cannot resolve a display unit, fall back to base-unit-only.
  const displayUnit = findUnit(requestedDisplayId) ?? baseUnit;
  const displayAbbr = unitLabel(displayUnit);

  const isBaseDisplay =
    !displayUnit || !baseUnitId || displayUnit.id === baseUnitId;

  if (isBaseDisplay) {
    return `${formatNumber(numericQty, locale, precision)} ${baseAbbr}`.trim();
  }

  const conv = conversions.find((c) => c.unitId === displayUnit!.id);
  const factor = conv ? toNumber(conv.baseUnitsPer) : NaN;

  if (!Number.isFinite(factor) || factor <= 0) {
    // No usable conversion — degrade to the base unit so we never lie.
    return `${formatNumber(numericQty, locale, precision)} ${baseAbbr}`.trim();
  }

  const displayQty = numericQty / factor;
  const primary = `${formatNumber(displayQty, locale, precision)} ${displayAbbr}`.trim();

  if (!opts.showSecondary) {
    return primary;
  }

  const secondary = `${formatNumber(numericQty, locale, precision)} ${baseAbbr}`.trim();
  return `${primary} · ${secondary}`;
}

/**
 * Lightweight companion used in places (notification snapshots, legacy
 * `unit` text columns) where we only know a quantity and a unit *label*,
 * not the full chain. Routes through the same Intl-based number formatter
 * so localization is consistent with `formatQty`.
 */
export function formatQtySnapshot(
  qty: number | string | null | undefined,
  unitText: string | null | undefined,
  opts: { locale?: "en" | "ar"; precision?: number } = {},
): string {
  const numericQty = toNumber(qty as any);
  const locale = opts.locale ?? "en";
  const precision = opts.precision ?? 2;
  if (!Number.isFinite(numericQty)) return "—";
  const label = (unitText || "").toString().trim();
  return label
    ? `${formatNumber(numericQty, locale, precision)} ${label}`
    : `${formatNumber(numericQty, locale, precision)}`;
}

/**
 * Returns just the display unit's label for an item, falling back to
 * base-unit then empty. Useful for input field suffixes where qty is
 * entered separately.
 */
export function formatUnitLabel(
  item: FormatQtyItem,
  units: FormatQtyUnit[],
  opts: { displayUnitId?: string | null } = {},
): string {
  const id = opts.displayUnitId ?? item.defaultDisplayUnitId ?? item.baseUnitId ?? null;
  if (!id) return "";
  const u = units.find((x) => x.id === id);
  return (u?.abbreviation || u?.name || "").toString();
}
